import React, { useMemo, useState } from "react";
import {
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
  CardBody,
  Col,
  Card,
} from "reactstrap";
import classnames from "classnames";
import SimpleBar from "simplebar-react";
import { Link } from "react-router-dom";
import type { RecentActivityRow } from "../../utils/dashboardMetrics";

interface TransactionsProps {
  rows: RecentActivityRow[];
  loading?: boolean;
}

const Transactions = ({ rows, loading }: TransactionsProps) => {
  const [activeTab, setActiveTab] = useState<string>("1");

  const toggle = (tab: any) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const filtered = useMemo(() => {
    if (activeTab === "2") {
      return rows.filter((row) =>
        ["COMPLETED", "PROCESSING", "CONFIRMED"].includes(row.statusKey)
      );
    }
    if (activeTab === "3") {
      return rows.filter((row) =>
        ["PENDING", "ON_HOLD", "CANCEL_REQUEST"].includes(row.statusKey)
      );
    }
    return rows;
  }, [activeTab, rows]);

  return (
    <React.Fragment>
      <Col xl={4}>
        <Card>
          <div className="card-header align-items-center d-flex">
            <h4 className="card-title mb-0 flex-grow-1">Recent Transactions</h4>
            <div className="flex-shrink-0">
              <Nav
                className="justify-content-end nav-tabs-custom rounded card-header-tabs"
                role="tablist"
              >
                <NavItem>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === "1" })}
                    onClick={() => {
                      toggle("1");
                    }}
                  >
                    All
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === "2" })}
                    onClick={() => {
                      toggle("2");
                    }}
                  >
                    Confirmed
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === "3" })}
                    onClick={() => {
                      toggle("3");
                    }}
                  >
                    Pending
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </div>

          <CardBody>
            <TabContent activeTab={activeTab}>
              <TabPane tabId={activeTab}>
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <SimpleBar style={{ maxHeight: "352px" }}>
                    <div className="table-responsive">
                      <table className="table align-middle table-nowrap mb-0">
                        <tbody>
                          {filtered.length === 0 ? (
                            <tr>
                              <td className="text-muted text-center py-4">
                                No data available for this period
                              </td>
                            </tr>
                          ) : (
                            filtered.map((row) => {
                              const positive = [
                                "COMPLETED",
                                "PROCESSING",
                                "CONFIRMED",
                              ].includes(row.statusKey);
                              return (
                                <tr key={`${row.orderId}-${row.reference}`}>
                                  <td>
                                    <div
                                      className={`font-size-22 text-${
                                        positive ? "success" : "warning"
                                      }`}
                                    >
                                      <i
                                        className={`bx bx-${
                                          positive ? "down" : "up"
                                        }-arrow-circle d-block`}
                                      ></i>
                                    </div>
                                  </td>
                                  <td>
                                    <div>
                                      <h5 className="font-size-14 mb-1">
                                        <Link
                                          to={`/orders/${row.orderId}`}
                                          className="text-dark"
                                        >
                                          {row.type} · {row.customer}
                                        </Link>
                                      </h5>
                                      <p className="text-muted mb-0 font-size-12">
                                        {row.date}
                                      </p>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="text-end">
                                      <h5 className="font-size-14 mb-0">
                                        {row.reference}
                                      </h5>
                                      <p className="text-muted mb-0 font-size-12">
                                        {row.status}
                                      </p>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="text-end">
                                      <h5 className="font-size-14 text-muted mb-0">
                                        {row.amount}
                                      </h5>
                                      <p className="text-muted mb-0 font-size-12">
                                        Amount
                                      </p>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </SimpleBar>
                )}
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default Transactions;
